import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../utils/api";
import { STATUS } from "../../../utils/status";

export const fetchWorkflowNodes = createAsyncThunk(
  "workflow/fetchNodes",
  async (projectId: string, thunkAPI) => {
    const response = await api.get(
      `/project/company/stage/?project_id=${projectId}`
    );
    const { status, data } = response;
    if (status === 200) {
      return { data };
    } else {
      return thunkAPI.rejectWithValue({ response, data });
    }
  }
);

export const fetchWorkflowLinks = createAsyncThunk(
  "workflow/fetchLinks",
  async (projectId: string, thunkAPI) => {
    const response = await api.get(
      `/project/company/stage/link?project_id=${projectId}&page_size=100`
    );
    const { status, data } = response;
    if (status === 200) {
      return { data };
    } else {
      return thunkAPI.rejectWithValue({ response, data });
    }
  }
);

export const updateWorkflow = createAsyncThunk(
  "workflow/updateLinks",
  async (payload: any, thunkAPI) => {
    try {
      const response = await api.patch(`project/company/stage/`, payload);
      const { status, data } = response;
      if (status === 200) {
        return { data, payload };
      } else {
        return thunkAPI.rejectWithValue({ response, data });
      }
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const updateWorkflowLinks = createAsyncThunk(
  'project/company/Stage/patch',
  async (payload: any, thunkAPI) => {
    const response = await api.patch(`project/company/stage/link/`, payload);
    const { status, data } = response;
    if (status === 200) {
      return { data, payload };
    } else {
      return thunkAPI.rejectWithValue({ response, data });
    }
  }
);

export const createWorkflowLink = createAsyncThunk(
  'project/company/stage/POST',
  async (payload: any, thunkAPI) => {
    const response = await api.post('project/company/stage/link/', payload);
    const { status, data } = response;
    if (status === 201) {
      return data;
    } else {
      return thunkAPI.rejectWithValue({ response, data });
    }
  }
)

export const getWorkflowStatus = createAsyncThunk(
  "workflow/getStatus",
  async (subProjectId: string, thunkAPI) => {
    try {
      const response = await api.get(`/project/company/workflow/${subProjectId}/`);
      const { status, data } = response;

      if (status === 200 && data?.data) {
        return data.data; // { sub_project_id, workflow_status }
      } else {
        return thunkAPI.rejectWithValue("Invalid response format");
      }
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch workflow status";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

interface WorkflowState {
  nodes: any[];
  links: any[];
  loading: boolean;
  error: string | null;
  updateStatusWorkflow: STATUS;
  workflowStatusLoading: boolean;
  workflowStatus: string | null;
  workflowStatusError: string | null;
}

const initialState: WorkflowState = {
  nodes: [],
  links: [],
  loading: false,
  error: null,
  updateStatusWorkflow: STATUS.NOT_STARTED,
  workflowStatus: null,
  workflowStatusLoading: false,
  workflowStatusError: null,
};

const workflowSlice = createSlice({
  name: "workflow",
  initialState,
  reducers: {
    setUpdateStatusWorkflowStatus(state, action) {
      state.updateStatusWorkflow = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkflowNodes.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWorkflowNodes.fulfilled, (state, action) => {
        state.loading = false;
        state.nodes = action.payload.data.results || [];
      })
      .addCase(fetchWorkflowNodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch workflow nodes";
      })
      .addCase(fetchWorkflowLinks.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWorkflowLinks.fulfilled, (state, action) => {
        state.loading = false;
        state.links = action.payload.data.results || [];
      })
      .addCase(fetchWorkflowLinks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch workflow links";
      })
      .addCase(updateWorkflow.pending, (state) => {
        state.loading = true;
        state.updateStatusWorkflow = STATUS.PENDING
      })
      .addCase(updateWorkflow.fulfilled, (state, action) => {
        state.loading = false;
        state.updateStatusWorkflow = STATUS.SUCESS
      })
      .addCase(updateWorkflow.rejected, (state, action) => {
        state.loading = false;
        state.updateStatusWorkflow = STATUS.FAILED
      })
      .addCase(getWorkflowStatus.pending, (state) => {
        state.workflowStatusLoading = true;
        state.workflowStatusError = "";
      })
      .addCase(getWorkflowStatus.fulfilled, (state, action) => {
        state.workflowStatusLoading = false;
        state.workflowStatus = action.payload.workflow_status;
      })
      .addCase(getWorkflowStatus.rejected, (state, action) => {
        state.workflowStatusLoading = false;
        state.workflowStatusError =
          (action.payload as string) || "Failed to fetch workflow status";
      });
  },
});

export const { setUpdateStatusWorkflowStatus } = workflowSlice.actions;
export default workflowSlice.reducer;
